import { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, Mail, ExternalLink, Search } from 'lucide-react';
import { emailLogApi, type EmailLogItem } from '../../api/email_log';

const KIND_LABEL: Record<string, string> = {
  client_reply: 'ответ клиенту',
  new_ticket: 'новый тикет',
  assignment: 'назначение',
  new_client_message: 'сообщение клиента',
  sla_breach: 'SLA',
  redmine_escalation: 'эскалация',
};

function SendBadge({ s }: { s: string }) {
  const map: Record<string, [string, string]> = {
    sent: ['Отправлено', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'],
    failed: ['Ошибка', 'bg-rose-500/15 text-rose-300 border-rose-500/30'],
    skipped: ['Пропущено', 'bg-amber-500/15 text-amber-300 border-amber-500/30'],
  };
  const [label, cls] = map[s] || [s, 'bg-slate-700 text-slate-300 border-slate-600'];
  return <span className={`px-2 py-0.5 rounded-full text-xs border ${cls}`}>{label}</span>;
}

function DeliveryBadge({ s }: { s: string }) {
  const map: Record<string, [string, string]> = {
    delivered: ['Доставлено', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'],
    bounced: ['Отклонено', 'bg-rose-500/15 text-rose-300 border-rose-500/30'],
    deferred: ['В очереди', 'bg-sky-500/15 text-sky-300 border-sky-500/30'],
    unknown: ['—', 'bg-slate-700 text-slate-400 border-slate-600'],
    no_data: ['нет данных', 'bg-slate-700 text-slate-500 border-slate-600'],
  };
  const [label, cls] = map[s] || [s, 'bg-slate-700 text-slate-300 border-slate-600'];
  return <span className={`px-2 py-0.5 rounded-full text-xs border ${cls}`}>{label}</span>;
}

function fmt(ts: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
}

export default function EmailLogEditor() {
  const [items, setItems] = useState<EmailLogItem[]>([]);
  const [counters, setCounters] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendFilter, setSendFilter] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await emailLogApi.list({ limit: 300, send_status: sendFilter || undefined, q: q || undefined });
      setItems(r.items);
      setCounters(r.counters);
    } finally {
      setLoading(false);
    }
  }, [sendFilter, q]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await emailLogApi.refresh();
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <div className="flex items-center justify-between p-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Mail className="text-blue-400" size={18} />
          <h2 className="text-lg font-semibold text-white">Журнал писем</h2>
        </div>
        <button onClick={refresh} disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50">
          {refreshing ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}
          Обновить доставку
        </button>
      </div>

      <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-800">
        <div className="flex gap-1 text-xs">
          {[['', 'Все'], ['sent', 'Отправленные'], ['failed', 'Ошибки'], ['skipped', 'Пропущенные']].map(([v, l]) => (
            <button key={v} onClick={() => setSendFilter(v)}
              className={`px-3 py-1.5 rounded-lg border ${sendFilter === v ? 'bg-blue-500/20 border-blue-500/40 text-blue-200' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск: адрес, тема, № тикета"
            className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500/50" />
        </div>
        <div className="flex gap-3 text-xs text-slate-400">
          <span>✅ доставлено: <b className="text-emerald-300">{counters.delivered ?? 0}</b></span>
          <span>❌ отклонено: <b className="text-rose-300">{counters.bounced ?? 0}</b></span>
          <span>⚠ пропущено: <b className="text-amber-300">{counters.skipped ?? 0}</b></span>
        </div>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-slate-500">Пока нет записей.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs border-b border-slate-800">
                <th className="px-4 py-2 font-medium">Время</th>
                <th className="px-4 py-2 font-medium">Тикет</th>
                <th className="px-4 py-2 font-medium">Кому</th>
                <th className="px-4 py-2 font-medium">Тип</th>
                <th className="px-4 py-2 font-medium">Отправка</th>
                <th className="px-4 py-2 font-medium">Доставка</th>
                <th className="px-4 py-2 font-medium">Детали</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                  <td className="px-4 py-2 text-slate-400 whitespace-nowrap tabular-nums">{fmt(it.created_at)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {it.ticket_id ? (
                      <a href={`/tickets/${it.ticket_id}`} className="text-blue-400 hover:underline inline-flex items-center gap-1">
                        {it.ticket_number || '—'}<ExternalLink size={11} />
                      </a>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-300 max-w-[220px] truncate" title={it.to_addr}>{it.to_addr}</td>
                  <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{KIND_LABEL[it.kind] || it.kind || '—'}</td>
                  <td className="px-4 py-2"><SendBadge s={it.send_status} /></td>
                  <td className="px-4 py-2">
                    {it.send_status === 'sent' ? <DeliveryBadge s={it.delivery_status} /> : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs max-w-[260px] truncate"
                    title={it.reason || it.delivery_detail}>
                    {it.reason || it.delivery_detail || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-xs text-slate-600 border-t border-slate-800">
        «Отправка» — SD сдал письмо в почтовый сервер. «Доставка» — подтверждение от конечного
        сервера получателя (из логов postfix). «Пропущено» — письмо не отправлялось (причина в «Деталях»).
      </div>
    </section>
  );
}
